/**
 * Prospection — the prospect window.
 *
 * Opened from a board row in the shared window component (movable, resizable,
 * can stay open next to the board). Three parts:
 *   - the head: who it is, how to reach them (click-to-call, mailto), the
 *     score and why, the owner and the stage — both editable in place;
 *   - the log form: one call / email / meeting / note, its outcome, and in
 *     the same breath the next action — a rep logs a call in one go;
 *   - the journal: everything the team did on this prospect, newest first,
 *     with the full customer history (emails, calls, orders) one fold away.
 *
 * Every save returns the updated row; the window patches itself and tells
 * the board (App 'prospection:updated'), so nothing is fetched twice.
 */
define('prospectionWindowView',[
  'require',
  'module',
  'app',
  'marionette',
  'underscore',
  'jquery',
  'settings',
  'rolesMixin',
  'entities/prospection',
  'entities/mails',
  'entities/customers/customers'
], function (require, Module, App, Marionette, _, $, Settings, RolesMixin) {
  'use strict';

  // Tell the team the file is open, and keep telling them while it is.
  var PRESENCE_MS = 3 * 60 * 1000;

  var STAGES = [
    { key: 'NEW', label: _.i18n('prospection.stage.NEW', null, 'New') },
    { key: 'CONTACTED', label: _.i18n('prospection.stage.CONTACTED', null, 'Contacted') },
    { key: 'INTERESTED', label: _.i18n('prospection.stage.INTERESTED', null, 'Interested') },
    { key: 'QUOTE_SENT', label: _.i18n('prospection.stage.QUOTE_SENT', null, 'Quote sent') },
    { key: 'ON_HOLD', label: _.i18n('prospection.stage.ON_HOLD', null, 'On hold') },
    { key: 'WON', label: _.i18n('prospection.stage.WON', null, 'Won') },
    { key: 'LOST', label: _.i18n('prospection.stage.LOST', null, 'Lost') }
  ];

  var PHONE_OUTCOMES = [
    { key: 'REACHED', label: _.i18n('prospection.phone.REACHED', null, 'Reached'), tone: 'ok' },
    { key: 'CALLBACK', label: _.i18n('prospection.phone.CALLBACK', null, 'Call back'), tone: 'warm' },
    { key: 'NO_ANSWER', label: _.i18n('prospection.phone.NO_ANSWER', null, 'No answer'), tone: 'try' },
    { key: 'VOICEMAIL', label: _.i18n('prospection.phone.VOICEMAIL', null, 'Voicemail'), tone: 'try' },
    { key: 'REFUSED', label: _.i18n('prospection.phone.REFUSED', null, 'Refused'), tone: 'bad' },
    { key: 'WRONG_NUMBER', label: _.i18n('prospection.phone.WRONG_NUMBER', null, 'Wrong number'), tone: 'bad' }
  ];

  var EMAIL_OUTCOMES = [
    { key: 'SENT', label: _.i18n('prospection.email.SENT', null, 'Sent'), tone: 'try' },
    { key: 'FOLLOW_UP', label: _.i18n('prospection.email.FOLLOW_UP', null, 'Follow-up sent'), tone: 'try' },
    { key: 'REPLIED', label: _.i18n('prospection.email.REPLIED', null, 'Replied'), tone: 'ok' },
    { key: 'BOUNCED', label: _.i18n('prospection.email.BOUNCED', null, 'Bounced'), tone: 'bad' },
    { key: 'REFUSED', label: _.i18n('prospection.email.REFUSED', null, 'Refused'), tone: 'bad' }
  ];

  var LOG_TYPES = [
    { key: 'CALL', label: _.i18n('prospection.log.call', null, 'Call'), icon: 'fa-phone' },
    { key: 'EMAIL', label: _.i18n('prospection.log.email', null, 'Email'), icon: 'fa-envelope-o' },
    { key: 'MEETING', label: _.i18n('prospection.log.meeting', null, 'Meeting'), icon: 'fa-calendar' },
    { key: 'NOTE', label: _.i18n('prospection.log.note', null, 'Note'), icon: 'fa-sticky-note-o' }
  ];

  var NEXT_TYPES = [
    { key: 'CALL', label: _.i18n('prospection.next.call', null, 'Call') },
    { key: 'EMAIL', label: _.i18n('prospection.next.email', null, 'Email') },
    { key: 'MEETING', label: _.i18n('prospection.next.meeting', null, 'Meeting') },
    { key: 'OTHER', label: _.i18n('prospection.next.other', null, 'Other') }
  ];

  // Quick picks for the next action date: what reps actually choose.
  var QUICK_DATES = [
    { key: 'tomorrow', label: _.i18n('prospection.quick.tomorrow', null, 'Tomorrow'), days: 1 },
    { key: '3d', label: _.i18n('prospection.quick.3d', null, 'In 3 days'), days: 3 },
    { key: '1w', label: _.i18n('prospection.quick.1w', null, 'Next week'), days: 7 },
    { key: '1m', label: _.i18n('prospection.quick.1m', null, 'In a month'), days: 30 }
  ];

  var EVENT_META = {
    CALL: { icon: 'fa-phone', cls: 'call', label: _.i18n('prospection.log.call', null, 'Call') },
    EMAIL: { icon: 'fa-envelope-o', cls: 'mail', label: _.i18n('prospection.log.email', null, 'Email') },
    MEETING: { icon: 'fa-calendar', cls: 'meet', label: _.i18n('prospection.log.meeting', null, 'Meeting') },
    NOTE: { icon: 'fa-sticky-note-o', cls: 'note', label: _.i18n('prospection.log.note', null, 'Note') },
    STAGE: { icon: 'fa-flag', cls: 'sys', label: _.i18n('prospection.journal.stage', null, 'Stage') },
    ASSIGN: { icon: 'fa-user', cls: 'sys', label: _.i18n('prospection.journal.assign', null, 'Owner') },
    NEXT_ACTION: { icon: 'fa-clock-o', cls: 'sys', label: _.i18n('prospection.journal.next', null, 'Next action') },
    ENROLLED: { icon: 'fa-plus-circle', cls: 'sys', label: _.i18n('prospection.journal.enrolled', null, 'Added to the board') }
  };

  function labelOf(list, key) {
    var found = _.findWhere(list, { key: key });
    return found ? found.label : (key || '');
  }

  function outcomeLabel(type, outcome) {
    if (!outcome) { return ''; }
    if (type === 'CALL') { return labelOf(PHONE_OUTCOMES, outcome); }
    if (type === 'EMAIL') { return labelOf(EMAIL_OUTCOMES, outcome); }
    if (type === 'STAGE') { return labelOf(STAGES, outcome); }
    if (type === 'NEXT_ACTION') { return labelOf(NEXT_TYPES, outcome); }
    return outcome;
  }

  function currentUserSecId() {
    var user = Settings.get('currentUser');
    return user ? (user.secId || null) : null;
  }

  function userDisplayName(u) {
    if (!u) { return ''; }
    var full = $.trim(((u.firstName || '') + ' ' + (u.lastName || '')).replace(/\s+/g, ' '));
    return u.fullName || full || u.email || u.logonId || u.secId || '';
  }

  function initials(name) {
    if (!name) { return '?'; }
    var parts = $.trim(name).split(/\s+/);
    if (parts.length === 1) { return parts[0].slice(0, 2).toUpperCase(); }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  function avatarColor(name) {
    var str = String(name || '');
    var hash = 0;
    for (var i = 0; i < str.length; i++) {
      hash = (hash * 31 + str.charCodeAt(i)) % 997;
    }
    return 'c' + (hash % 10);
  }

  function formatDate(value) {
    if (!value) { return ''; }
    var d = new Date(value);
    if (isNaN(d.getTime())) { return ''; }
    return d.toLocaleString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  function formatDay(value) {
    if (!value) { return '—'; }
    var d = new Date(value);
    if (isNaN(d.getTime())) { return '—'; }
    return d.toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  function formatAmount(value) {
    if (value === null || value === undefined || value === '') { return '—'; }
    var v = Math.round(Number(value) || 0);
    return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' €';
  }

  // "2026-09-18T09:00" for a datetime-local input, in local time.
  function toLocalInput(d) {
    function two(n) { return (n < 10 ? '0' : '') + n; }
    return d.getFullYear() + '-' + two(d.getMonth() + 1) + '-' + two(d.getDate()) + 'T' + two(d.getHours()) + ':' + two(d.getMinutes());
  }

  function inDays(days) {
    var d = new Date();
    d.setDate(d.getDate() + days);
    d.setHours(9, 0, 0, 0);
    return d;
  }

  // What 3CX's dialer wants: digits, a leading + at most. "02/123.45.67" -> "021234567",
  // "+32 (0)2 123 45 67" -> "+3202..." is left to 3CX; "0032..." becomes "+32...".
  function dialable(tel) {
    var s = String(tel || '').replace(/[^0-9+]/g, '');
    if (s.indexOf('00') === 0) { s = '+' + s.slice(2); }
    if (s.indexOf('+') > 0) { s = s.replace(/\+/g, ''); }
    return s;
  }

  function gaugeFill(score) {
    return Math.max(4, Math.min(100, Math.round((score || 0))));
  }

  function gaugeHeat(score) {
    if (score >= 60) { return 'hot'; }
    if (score >= 35) { return 'warm'; }
    return 'cool';
  }

  var ProspectionWindowView = Marionette.ItemView.extend({
    className: 'prospection-window',

    template: _.template(
      '<div class="pw-head">' +
      '  <span class="pw-av <%= avatarColor %>"><%= avatar %></span>' +
      '  <div class="pw-who">' +
      '    <div class="pw-title"><%= name %>' +
      '      <% if (isLead) { %><span class="pw-tag lead"><%= leadLabel %></span><% } else { %><span class="pw-tag cust"><%= customerLabel %></span><% } %>' +
      '      <% if (vip) { %><span class="pw-tag vip">VIP</span><% } %>' +
      '    </div>' +
      '    <div class="pw-contact">' +
      '      <% if (person) { %><span><i class="fa fa-user-o"></i><%= person %></span><% } %>' +
      '      <% if (tel) { %><a class="js-call" data-tel="<%= tel %>" title="<%= callTitle %>"><i class="fa fa-phone"></i><%= tel %></a><% } %>' +
      '      <% if (mobile) { %><a class="js-call" data-tel="<%= mobile %>" title="<%= callTitle %>"><i class="fa fa-mobile"></i><%= mobile %></a><% } %>' +
      '      <% if (email) { %><a class="js-mail" data-email="<%= email %>" title="<%= mailTitle %>"><i class="fa fa-envelope-o"></i><%= email %></a><% } %>' +
      '      <% if (place) { %><span><i class="fa fa-map-marker"></i><%= place %></span><% } %>' +
      '      <% if (webSite) { %><a href="<%= webSiteHref %>" target="_blank" rel="noopener"><i class="fa fa-globe"></i><%= webSite %></a><% } %>' +
      '    </div>' +
      '  </div>' +
      '  <span class="pw-gauge <%= heat %>" style="--p:<%= fill %>" title="<%= scoreReason %>"><i><%= score %></i></span>' +
      '  <div class="pw-controls">' +
      '    <label><%= ownerLabel %><select class="js-owner"><option value=""><%= nobody %></option></select></label>' +
      '    <label><%= stageLabel %><select class="js-stage">' +
      '      <% _.each(stages, function (s) { %><option value="<%= s.key %>" <%= s.key === stage ? "selected" : "" %>><%= s.label %></option><% }); %>' +
      '    </select></label>' +
      '  </div>' +
      '  <div class="pw-links">' +
      '    <button type="button" class="pw-link js-open-customer"><i class="fa fa-id-card-o"></i> <%= fileLabel %></button>' +
      '    <% if (companySecId) { %><button type="button" class="pw-link js-open-company"><i class="fa fa-building-o"></i> <%= companyLabel %></button><% } %>' +
      '    <button type="button" class="pw-link quiet js-refresh" title="<%= refreshTitle %>"><i class="fa fa-refresh"></i></button>' +
      '    <button type="button" class="pw-link quiet danger js-remove" title="<%= removeTitle %>"><i class="fa fa-trash-o"></i></button>' +
      '  </div>' +
      '</div>' +
      '<div class="pw-facts js-facts"></div>' +
      '<div class="pw-body">' +
      '  <div class="pw-col pw-log">' +
      '    <div class="pw-next js-next"></div>' +
      '    <h6><%= logTitle %></h6>' +
      '    <div class="pw-types">' +
      '      <% _.each(logTypes, function (t) { %><button type="button" class="pw-type js-type <%= t.key === "CALL" ? "on" : "" %>" data-type="<%= t.key %>"><i class="fa <%= t.icon %>"></i><%= t.label %></button><% }); %>' +
      '    </div>' +
      '    <div class="pw-outcomes js-outcomes"></div>' +
      '    <textarea class="js-note" rows="3" placeholder="<%= notePlaceholder %>"></textarea>' +
      '    <div class="pw-plan">' +
      '      <span class="pw-plan-l"><i class="fa fa-clock-o"></i><%= nextLabel %></span>' +
      '      <select class="js-next-type"><% _.each(nextTypes, function (t) { %><option value="<%= t.key %>"><%= t.label %></option><% }); %></select>' +
      '      <input type="datetime-local" class="js-next-at"/>' +
      '      <% _.each(quickDates, function (q) { %><button type="button" class="pw-quick js-quick" data-days="<%= q.days %>"><%= q.label %></button><% }); %>' +
      '      <input type="text" class="js-next-note" placeholder="<%= nextNotePlaceholder %>"/>' +
      '    </div>' +
      '    <div class="pw-plan-more">' +
      '      <label><%= moveToLabel %><select class="js-move"><option value=""><%= keepStage %></option>' +
      '        <% _.each(stages, function (s) { %><option value="<%= s.key %>"><%= s.label %></option><% }); %></select></label>' +
      '      <label class="js-claim-wrap" style="display:none"><input type="checkbox" class="js-claim" checked/><%= claimLabel %></label>' +
      '      <span class="pw-grow"></span>' +
      '      <button type="button" class="pw-save js-save"><i class="fa fa-check"></i> <%= saveLabel %></button>' +
      '    </div>' +
      '    <div class="pw-error js-error"></div>' +
      '  </div>' +
      '  <div class="pw-col pw-journal">' +
      '    <h6><%= journalTitle %></h6>' +
      '    <ul class="pw-events js-events"><li class="pw-state"><%= loading %></li></ul>' +
      '    <% if (hasHistory) { %>' +
      '    <div class="pw-history js-history is-folded">' +
      '      <h6 class="js-history-toggle" tabindex="0" role="button"><i class="fa fa-caret-right"></i><%= historyTitle %></h6>' +
      '      <div class="js-history-host"></div>' +
      '    </div>' +
      '    <% } %>' +
      '  </div>' +
      '</div>'
    ),

    ui: {
      facts: '.js-facts',
      next: '.js-next',
      events: '.js-events',
      outcomes: '.js-outcomes',
      note: '.js-note',
      nextType: '.js-next-type',
      nextAt: '.js-next-at',
      nextNote: '.js-next-note',
      move: '.js-move',
      claimWrap: '.js-claim-wrap',
      claim: '.js-claim',
      owner: '.js-owner',
      stage: '.js-stage',
      error: '.js-error',
      save: '.js-save',
      history: '.js-history',
      historyHost: '.js-history-host'
    },

    events: {
      'click .js-type': 'onType',
      'click .js-outcome': 'onOutcome',
      'click .js-quick': 'onQuickDate',
      'click .js-save': 'onSave',
      'change .js-owner': 'onOwnerChange',
      'change .js-stage': 'onStageChange',
      'click .js-call': 'onCall',
      'click .js-mail': 'onMail',
      'click .js-open-customer': 'onOpenCustomer',
      'click .js-open-company': 'onOpenCompany',
      'click .js-refresh': 'onRefresh',
      'click .js-remove': 'onRemove',
      'click .js-next-done': 'onNextDone',
      'click .js-next-clear': 'onNextClear',
      'click .js-history-toggle': 'onToggleHistory',
      'keydown .js-history-toggle': 'onToggleHistoryKey'
    },

    /** options.secId, and optionally options.item (the board row, to paint at once). */
    initialize: function (options) {
      this.secId = options.secId;
      this.item = options.item || null;
      this.logType = 'CALL';
      this.outcome = null;
      this.users = [];
    },

    serializeData: function () {
      var p = this.item || {};
      var name = p.customerName || p.companyName || p.customerEmail || p.customerEndUserId || '…';
      var person = $.trim((p.customerFirstName || '') + ' ' + (p.customerLastName || ''));
      var place = _.compact([p.customerCity, p.customerCountryCode ? String(p.customerCountryCode).toUpperCase() : '']).join(', ');
      var site = p.customerWebSite || p.companyWebSite || '';
      return {
        name: name,
        avatar: initials(name),
        avatarColor: avatarColor(name),
        isLead: p.customerType === 'lead',
        leadLabel: _.i18n('prospection.lead', null, 'Lead'),
        customerLabel: _.i18n('prospection.customer', null, 'Customer'),
        vip: !!p.customerVip,
        person: person,
        tel: p.customerTel || '',
        mobile: p.customerMobile || '',
        email: p.customerEmail || '',
        place: place,
        webSite: site,
        webSiteHref: /^https?:\/\//i.test(site) ? site : 'http://' + site,
        callTitle: _.i18n('prospection.call', null, 'Call with 3CX'),
        mailTitle: _.i18n('prospection.mail', null, 'Write an email (from the dashboard, with your signature)'),
        score: p.score || 0,
        fill: gaugeFill(p.score),
        heat: gaugeHeat(p.score || 0),
        scoreReason: p.scoreReason || '',
        ownerLabel: _.i18n('prospection.col.owner', null, 'Owner'),
        nobody: _.i18n('prospection.nobody', null, 'Nobody'),
        stageLabel: _.i18n('prospection.col.stage', null, 'Stage'),
        stages: STAGES,
        stage: p.stage || 'NEW',
        companySecId: p.companySecId || '',
        fileLabel: p.customerType === 'lead' ?
          _.i18n('prospection.openLead', null, 'Lead file') : _.i18n('prospection.openCustomer', null, 'Customer file'),
        companyLabel: _.i18n('prospection.openCompany', null, 'Company'),
        refreshTitle: _.i18n('prospection.refresh', null, 'Recompute figures and score'),
        removeTitle: _.i18n('prospection.remove', null, 'Take off the board'),
        logTitle: _.i18n('prospection.log.title', null, 'Log an activity'),
        logTypes: LOG_TYPES,
        notePlaceholder: _.i18n('prospection.log.note.placeholder', null, 'What was said, what they need, who to ask for…'),
        nextLabel: _.i18n('prospection.next.label', null, 'Next'),
        nextTypes: NEXT_TYPES,
        quickDates: QUICK_DATES,
        nextNotePlaceholder: _.i18n('prospection.next.note', null, 'What to do next (optional)'),
        moveToLabel: _.i18n('prospection.moveTo', null, 'Move to'),
        keepStage: _.i18n('prospection.keepStage', null, 'Keep stage'),
        claimLabel: _.i18n('prospection.claim.me', null, 'Make me the owner'),
        saveLabel: _.i18n('common.save', null, 'Save'),
        journalTitle: _.i18n('prospection.journal', null, 'Journal'),
        loading: _.i18n('common.loading', null, 'Loading…'),
        hasHistory: !!(Settings.get('cAccountRoles') || {})[RolesMixin.DASHBOARD_SALES_INBOX],
        historyTitle: _.i18n('prospection.history', null, 'Full history — emails, calls, orders, offers')
      };
    },

    onShow: function () {
      this.renderOutcomes();
      this.renderFacts();
      this.renderNext();
      this.loadUsers();
      this.fetchDetail();
      this.touch();
      this.presenceTimer = window.setInterval(_.bind(this.touch, this), PRESENCE_MS);
    },

    // ---- data ---------------------------------------------------------------
    fetchDetail: function () {
      var self = this;
      App.request('prospection:detail', this.secId).done(function (detail) {
        if (self.isDestroyed) { return; }
        self.detail = detail || {};
        var fresh = self.detail.prospection;
        var repaint = !self.item || !fresh || fresh.stage !== self.item.stage || fresh.score !== self.item.score;
        self.item = fresh || self.item;
        if (repaint) { self.repaint(); }
        self.renderEvents();
      }).fail(function () {
        if (self.isDestroyed) { return; }
        self.ui.events.html('<li class="pw-state bad">' + _.i18n('prospection.loadError', null, 'Could not load this prospect.') + '</li>');
      });
    },

    loadUsers: function () {
      var self = this;
      require(['entities/kanban_tickets'], function () {
        $.when(App.request('kanban-tickets:get-users')).done(function (users) {
          if (self.isDestroyed) { return; }
          self.users = users || [];
          self.renderOwner();
        });
      });
    },

    touch: function () {
      if (this.isDestroyed) { return; }
      App.request('prospection:touch', this.secId);
    },

    // The row changed (a save, a refresh): re-render everything but the form,
    // so what the rep is typing survives the round trip.
    applyRow: function (json) {
      if (!json) { return; }
      this.item = json;
      this.repaint();
      App.trigger('prospection:updated', json);
    },

    repaint: function () {
      var note = this.ui.note.val();
      var nextAt = this.ui.nextAt.val();
      var nextNote = this.ui.nextNote.val();
      var nextType = this.ui.nextType.val();
      var type = this.logType;
      var outcome = this.outcome;
      this.render();
      this.renderOutcomes();
      this.renderFacts();
      this.renderNext();
      this.renderOwner();
      this.renderEvents();
      this.ui.note.val(note);
      this.ui.nextAt.val(nextAt);
      this.ui.nextNote.val(nextNote);
      this.ui.nextType.val(nextType);
      this.setLogType(type);
      this.setOutcome(outcome);
    },

    // ---- rendering ----------------------------------------------------------
    renderOwner: function () {
      var p = this.item || {};
      var me = currentUserSecId();
      var current = p.assignedUser ? p.assignedUser.secId : '';
      var options = ['<option value="">' + _.i18n('prospection.nobody', null, 'Nobody') + '</option>'];
      var seen = {};
      var sorted = _.sortBy(this.users, function (u) { return userDisplayName(u).toLowerCase(); });
      _.each(sorted, function (u) {
        if (!u || !u.secId) { return; }
        seen[u.secId] = true;
        var label = userDisplayName(u) + (u.secId === me ? ' (' + _.i18n('common.me', null, 'me') + ')' : '');
        options.push('<option value="' + _.escape(u.secId) + '"' + (u.secId === current ? ' selected' : '') + '>' + _.escape(label) + '</option>');
      });
      // The owner may not be in the users list (left the account): keep them visible.
      if (current && !seen[current]) {
        options.push('<option value="' + _.escape(current) + '" selected>' + _.escape(userDisplayName(p.assignedUser)) + '</option>');
      }
      this.ui.owner.html(options.join(''));
      this.ui.claimWrap.toggle(!current);
    },

    renderFacts: function () {
      var p = this.item || {};
      var employees = p.companyEmployees || p.customerNbrEmployees;
      var facts = [
        { l: _.i18n('prospection.fact.orders', null, 'Orders'), v: (p.ordersCount || 0) + ' · ' + formatAmount(p.ordersTotal || 0),
          s: p.lastOrderAt ? _.i18n('prospection.fact.last', null, 'last') + ' ' + formatDay(p.lastOrderAt) : _.i18n('prospection.fact.never', null, 'never ordered'),
          tone: p.ordersCount ? 'good' : 'plain' },
        { l: _.i18n('prospection.fact.offers', null, 'Offers'), v: String(p.offersCount || 0),
          s: p.openOffersCount ? p.openOffersCount + ' ' + _.i18n('prospection.fact.open', null, 'in the last 90 days') :
            (p.lastOfferAt ? _.i18n('prospection.fact.last', null, 'last') + ' ' + formatDay(p.lastOfferAt) : '—'),
          tone: p.openOffersCount ? 'warm' : 'plain' },
        { l: _.i18n('prospection.fact.company', null, 'Company'), v: employees ? employees + ' ' + _.i18n('prospection.fact.employees', null, 'employees') : '—',
          s: _.compact([p.companyIndustry, p.companyAnnualRevenue ? formatAmount(p.companyAnnualRevenue) + '/y' : '']).join(' · ') || (p.companyName || '—'),
          tone: 'plain' },
        { l: _.i18n('prospection.fact.emails', null, 'Emails'),
          v: p.lastEmailInAt ? _.i18n('prospection.fact.in', null, 'in') + ' ' + formatDay(p.lastEmailInAt) : _.i18n('prospection.fact.noneIn', null, 'none received'),
          s: p.lastEmailOutAt ? _.i18n('prospection.fact.out', null, 'out') + ' ' + formatDay(p.lastEmailOutAt) : _.i18n('prospection.fact.noneOut', null, 'none sent'),
          tone: 'plain' },
        { l: _.i18n('prospection.col.phone', null, 'Phone'), v: labelOf(PHONE_OUTCOMES, p.phoneStatus) || _.i18n('prospection.phone.NOT_CALLED', null, 'Not called'),
          s: (p.phoneAttempts || 0) + ' ' + _.i18n('prospection.attempts', null, 'attempts') + (p.lastPhoneAt ? ' · ' + formatDay(p.lastPhoneAt) : ''),
          tone: p.phoneStatus === 'REACHED' || p.phoneStatus === 'CALLBACK' ? 'good' : (p.phoneStatus === 'REFUSED' || p.phoneStatus === 'WRONG_NUMBER' ? 'bad' : 'plain') },
        { l: _.i18n('prospection.col.email', null, 'Email'), v: labelOf(EMAIL_OUTCOMES, p.emailStatus) || _.i18n('prospection.email.NOT_SENT', null, 'Not sent'),
          s: (p.emailAttempts || 0) + ' ' + _.i18n('prospection.fact.sent', null, 'sent') + (p.lastEmailAt ? ' · ' + formatDay(p.lastEmailAt) : ''),
          tone: p.emailStatus === 'REPLIED' ? 'good' : (p.emailStatus === 'BOUNCED' || p.emailStatus === 'REFUSED' ? 'bad' : 'plain') }
      ];
      this.ui.facts.html(_.map(facts, function (f) {
        return '<div class="pw-fact ' + f.tone + '"><small>' + _.escape(f.l) + '</small><b>' + _.escape(f.v) + '</b><span>' + _.escape(f.s) + '</span></div>';
      }).join(''));
    },

    renderNext: function () {
      var p = this.item || {};
      if (!p.nextActionAt) {
        this.ui.next.html('').hide();
        return;
      }
      var tone = p.overdue ? 'bad' : (p.nextActionInDays === 0 ? 'today' : 'ok');
      this.ui.next.html(
        '<span class="pw-next-pill ' + tone + '"><i class="fa fa-clock-o"></i>' +
        _.escape(labelOf(NEXT_TYPES, p.nextActionType) || _.i18n('prospection.next.label', null, 'Next')) +
        ' — ' + _.escape(formatDate(p.nextActionAt)) +
        (p.overdue ? ' · ' + _.i18n('prospection.due.lateLong', null, 'late') : '') + '</span>' +
        (p.nextActionNote ? '<span class="pw-next-note">' + _.escape(p.nextActionNote) + '</span>' : '') +
        '<span class="pw-grow"></span>' +
        '<button type="button" class="pw-mini js-next-done" title="' + _.i18n('prospection.next.done.title', null, 'Mark as done (clears the plan)') + '">' +
        '<i class="fa fa-check"></i> ' + _.i18n('prospection.next.done', null, 'Done') + '</button>' +
        '<button type="button" class="pw-mini quiet js-next-clear">' + _.i18n('common.clear', null, 'Clear') + '</button>'
      ).show();
    },

    renderOutcomes: function () {
      var list = this.logType === 'CALL' ? PHONE_OUTCOMES : (this.logType === 'EMAIL' ? EMAIL_OUTCOMES : []);
      if (!list.length) {
        this.ui.outcomes.html('').hide();
        return;
      }
      this.ui.outcomes.html(_.map(list, function (o) {
        return '<button type="button" class="pw-outcome js-outcome ' + o.tone + '" data-key="' + o.key + '">' + _.escape(o.label) + '</button>';
      }).join('')).show();
      // The saved status is the natural default: a second "no answer" is one click.
      var p = this.item || {};
      var current = this.logType === 'CALL' ? p.phoneStatus : p.emailStatus;
      this.setOutcome(_.findWhere(list, { key: current }) ? current : null);
    },

    renderEvents: function () {
      var events = (this.detail && this.detail.activities) || [];
      if (!events.length) {
        this.ui.events.html('<li class="pw-state">' + _.i18n('prospection.journal.empty', null, 'Nothing logged yet. Log the first call or email on the left.') + '</li>');
        return;
      }
      this.ui.events.html(_.map(events, function (ev) {
        var meta = EVENT_META[ev.type] || { icon: 'fa-circle-o', cls: 'sys', label: ev.type };
        var who = userDisplayName(ev.user) || _.i18n('prospection.journal.system', null, 'System');
        var outcome = outcomeLabel(ev.type, ev.outcome);
        return '<li class="pw-ev ' + meta.cls + '">' +
          '<span class="pw-ev-ic"><i class="fa ' + meta.icon + '"></i></span>' +
          '<span class="pw-ev-main">' +
          '  <span class="pw-ev-t"><b>' + _.escape(meta.label) + '</b>' +
          (outcome ? '<span class="pw-ev-out">' + _.escape(outcome) + '</span>' : '') +
          (ev.nextActionAt ? '<span class="pw-ev-next"><i class="fa fa-clock-o"></i>' + _.escape(formatDate(ev.nextActionAt)) + '</span>' : '') +
          '  </span>' +
          (ev.note ? '<span class="pw-ev-note">' + _.escape(ev.note).replace(/\n/g, '<br/>') + '</span>' : '') +
          '  <span class="pw-ev-meta">' + _.escape(who) + ' · ' + _.escape(formatDate(ev.activityAt)) + '</span>' +
          '</span></li>';
      }).join(''));
    },

    // ---- the log form -------------------------------------------------------
    setLogType: function (type) {
      this.logType = type || 'CALL';
      this.$('.js-type').removeClass('on');
      this.$('.js-type[data-type="' + this.logType + '"]').addClass('on');
    },

    setOutcome: function (key) {
      this.outcome = key || null;
      this.$('.js-outcome').removeClass('on');
      if (key) { this.$('.js-outcome[data-key="' + key + '"]').addClass('on'); }
    },

    onType: function (e) {
      this.setLogType($(e.currentTarget).attr('data-type'));
      this.renderOutcomes();
    },

    onOutcome: function (e) {
      var key = $(e.currentTarget).attr('data-key');
      this.setOutcome(this.outcome === key ? null : key);
      // "Call back" and "reached" almost always mean a next step: pre-fill it.
      if (key === 'CALLBACK' && !this.ui.nextAt.val()) {
        this.ui.nextType.val('CALL');
        this.ui.nextAt.val(toLocalInput(inDays(1)));
      }
    },

    onQuickDate: function (e) {
      var days = parseInt($(e.currentTarget).attr('data-days'), 10) || 1;
      this.ui.nextAt.val(toLocalInput(inDays(days)));
    },

    onSave: function () {
      var self = this;
      var body = {
        type: this.logType,
        outcome: this.outcome,
        note: $.trim(this.ui.note.val()) || null,
        stage: this.ui.move.val() || null,
        claim: this.ui.claimWrap.is(':visible') && this.ui.claim.is(':checked')
      };
      if ((this.logType === 'CALL' || this.logType === 'EMAIL') && !this.outcome) {
        this.ui.error.text(_.i18n('prospection.log.needOutcome', null, 'Pick what the call / email gave.')).show();
        return;
      }
      if (this.logType === 'NOTE' && !body.note) {
        this.ui.error.text(_.i18n('prospection.log.needNote', null, 'Write the note first.')).show();
        return;
      }
      var nextAt = this.ui.nextAt.val();
      if (nextAt) {
        var d = new Date(nextAt);
        if (!isNaN(d.getTime())) {
          body.nextActionAt = d.getTime();
          body.nextActionType = this.ui.nextType.val();
          body.nextActionNote = $.trim(this.ui.nextNote.val()) || null;
        }
      }
      this.ui.error.hide();
      this.ui.save.prop('disabled', true);
      App.request('prospection:activity', this.secId, body).done(function (json) {
        if (self.isDestroyed) { return; }
        self.ui.save.prop('disabled', false);
        self.ui.note.val('');
        self.ui.nextAt.val('');
        self.ui.nextNote.val('');
        self.ui.move.val('');
        self.applyRow(json);
        self.fetchDetail();
      }).fail(function () {
        if (self.isDestroyed) { return; }
        self.ui.save.prop('disabled', false);
        self.ui.error.text(_.i18n('prospection.log.failed', null, 'Could not save. Try again.')).show();
      });
    },

    // ---- head controls ------------------------------------------------------
    onOwnerChange: function (e) {
      var self = this;
      var value = $(e.currentTarget).val() || null;
      App.request('prospection:assign', this.secId, value).done(function (json) {
        if (self.isDestroyed) { return; }
        self.applyRow(json);
        self.fetchDetail();
      });
    },

    onStageChange: function (e) {
      var self = this;
      var stage = $(e.currentTarget).val();
      var reason = null;
      if (stage === 'LOST') {
        reason = window.prompt(_.i18n('prospection.lostReason', null, 'Why is it lost? (optional)'), '');
      }
      App.request('prospection:stage', this.secId, stage, reason, null).done(function (json) {
        if (self.isDestroyed) { return; }
        self.applyRow(json);
        self.fetchDetail();
      });
    },

    onNextDone: function () {
      var self = this;
      App.request('prospection:next-action', this.secId, { clearNextAction: true }).done(function (json) {
        if (self.isDestroyed) { return; }
        self.applyRow(json);
        self.fetchDetail();
      });
    },

    onNextClear: function () {
      this.onNextDone();
    },

    // Same dialer the header's phone widget opens (Settings.threeCxDialerUrl),
    // called directly: the widget is not on every screen, so its event could
    // fall on nobody's ears.
    onCall: function (e) {
      e.preventDefault();
      e.stopPropagation();
      var tel = dialable($(e.currentTarget).attr('data-tel'));
      if (!tel) { return; }
      var dialerUrl = Settings.get('threeCxDialerUrl');
      if (dialerUrl) {
        window.open(dialerUrl + '?phone=' + encodeURIComponent(tel), '_blank');
      } else {
        App.trigger('phone:numbering', { phoneNumber: tel, country: null });
      }
    },

    // A new mail from the dashboard, like the "new mail" of the mails screen:
    // the account's sender for the signature template, the signature filled
    // with the customer's data, the prospect as recipient. Once sent, the
    // email is logged on the prospect by itself.
    onMail: function (e) {
      e.preventDefault();
      e.stopPropagation();
      var self = this;
      var to = $(e.currentTarget).attr('data-email');
      var p = this.item || {};
      if (!to || !p.customerEndUserId) { return; }
      var locale = String(p.customerLocale || Settings.get('lang') || 'fr').toLowerCase();
      require(['ordersMailsUtils', 'emailView', 'modalsAlertView'], function (OrdersMailsUtils, EmailView, AlertView) {
        if (self.isDestroyed) { return; }
        var customer = App.request('customers:entity:end-user-id', p.customerEndUserId);
        customer.fetch().done(function () {
          if (self.isDestroyed) { return; }
          var from = OrdersMailsUtils.getFrom('MAIL_SIGN', locale);
          OrdersMailsUtils.checkFrom(from).done(function () {
            EmailView.prototype.open({
              from: from,
              to: to,
              allowFromSelection: true,
              title: p.customerName || to,
              id: 'prospection-mail.' + self.secId,
              templateType: 'MAIL_SIGN',
              fetchTemplateData: function () {
                return $.Deferred().resolve({ customer: customer.toJSON() }).promise();
              },
              templateLocale: locale,
              // `sent` is the recipients string; the second mail to a prospect is a follow-up.
              onMailSend: function (sent) {
                var current = (self.item || {}).emailStatus;
                App.request('prospection:activity', self.secId, {
                  type: 'EMAIL',
                  outcome: current === 'SENT' || current === 'FOLLOW_UP' || current === 'REPLIED' ? 'FOLLOW_UP' : 'SENT',
                  note: _.i18n('prospection.mail.sentNote', null, 'Email sent from the dashboard') + (sent ? ' → ' + sent : ''),
                  claim: true
                }).done(function (json) {
                  if (self.isDestroyed) { return; }
                  self.applyRow(json);
                  self.fetchDetail();
                });
              }
            });
          }).fail(function () {
            AlertView.prototype.open('', _.i18n('common.googleFromEmailError').replace('%s', from));
          });
        });
      });
    },

    onOpenCustomer: function () {
      var p = this.item || {};
      if (!p.customerSecId) { return; }
      var base = p.customerType === 'lead' ? 'leads/' : 'customers/';
      App.navigate(base + p.customerSecId + '/profile', { trigger: true });
    },

    onOpenCompany: function () {
      var p = this.item || {};
      if (!p.companySecId) { return; }
      App.trigger('companies:show', p.companySecId, 'subaccounts');
    },

    onRefresh: function () {
      var self = this;
      App.request('prospection:refresh', this.secId).done(function (json) {
        if (self.isDestroyed) { return; }
        self.applyRow(json);
      });
    },

    onRemove: function () {
      var self = this;
      if (!window.confirm(_.i18n('prospection.remove.confirm', null, 'Take this prospect off the board? The customer itself is kept.'))) {
        return;
      }
      App.request('prospection:remove', this.secId).done(function () {
        App.trigger('prospection:removed', self.secId);
        require(['windows'], function (Windows) {
          _.each((Windows.views || []).slice(), function (windowView) {
            if ((windowView.id || '') === 'prospection:' + self.secId) { Windows.close(windowView); }
          });
        });
      });
    },

    // ---- history fold -------------------------------------------------------
    onToggleHistory: function () {
      var self = this;
      var folded = this.ui.history.hasClass('is-folded');
      this.ui.history.toggleClass('is-folded', !folded);
      this.$('.js-history-toggle .fa').toggleClass('fa-caret-right', !folded).toggleClass('fa-caret-down', folded);
      if (folded && !this.historyView) {
        var p = this.item || {};
        require(['salesInboxWorklistEmbedView'], function (EmbedView) {
          if (self.isDestroyed || self.historyView) { return; }
          self.historyView = new EmbedView({
            customerSecId: p.customerSecId,
            name: p.customerName
          });
          self.historyView.render();
          self.ui.historyHost.append(self.historyView.el);
          self.historyView.triggerMethod('show');
        });
      }
    },

    onToggleHistoryKey: function (e) {
      if (e.keyCode !== 13 && e.keyCode !== 32) { return; }
      e.preventDefault();
      this.onToggleHistory();
    },

    onDestroy: function () {
      if (this.presenceTimer) { window.clearInterval(this.presenceTimer); }
      if (this.historyView) { this.historyView.destroy(); this.historyView = null; }
    },

    /** Open the prospect in its own window (from a board row, or from a URL). */
    open: function (secId, item) {
      if (!secId) { return; }
      require(['windows'], function (Windows) {
        Windows.open({
          view: function () { return new ProspectionWindowView({ secId: secId, item: item || null }); },
          navigation: function () {
            return $.Deferred().resolve('prospection/' + secId).promise();
          },
          id: 'prospection:' + secId,
          className: 'prospection-window-frame',
          title: (item && (item.customerName || item.companyName || item.customerEmail)) || _.i18n('menu.prospection', null, 'Prospection'),
          width: '88%',
          height: '92%'
        });
      });
    }
  });

  Module.exports = ProspectionWindowView;
});

